import { DecalItem, DecalCategory } from '../../types/gallery';

export interface DecalPreset {
  category: DecalCategory;
  src: string;
  alt: string;
  defaultSize: number;
  maxRotate?: number; // 0 to 360, allowing custom +/- rotation limits
}

/**
 * Resolves the effective max +/- rotation limit for any decal image.
 * - If explicitMaxRotate is passed (0 to 360), it is clamped and returned.
 * - If the image is maggots.png (or contains 'maggots'), returns 10 (+-10 deg).
 * - If blood or paint splatter, returns 15 (+-15 deg gravity physics).
 * - Otherwise defaults to 360 (full rotation).
 */
export function getMaxRotateForImage(
  imageSrc?: string,
  explicitMaxRotate?: number,
  category?: string
): number {
  if (typeof explicitMaxRotate === 'number' && !isNaN(explicitMaxRotate)) {
    return Math.min(360, Math.max(0, explicitMaxRotate));
  }
  if (imageSrc) {
    const lower = imageSrc.toLowerCase();
    if (lower.includes('maggots')) {
      return 10;
    }
  }
  if (category === 'blood' || category === 'paint') {
    return 15;
  }
  return 360;
}

export const DECAL_PRESETS: DecalPreset[] = [
  // Blood Splatters (gravity physics: max +/- 15 deg)
  {
    category: 'blood',
    src: '/Images/Blood Splatters/1.png',
    alt: 'blood splatter decal',
    defaultSize: 170,
    maxRotate: 15
  },
  {
    category: 'blood',
    src: '/Images/Blood Splatters/2.png',
    alt: 'blood splatter decal',
    defaultSize: 165,
    maxRotate: 15
  },
  {
    category: 'blood',
    src: '/Images/Blood Splatters/3.png',
    alt: 'blood splatter decal',
    defaultSize: 180,
    maxRotate: 15
  },
  {
    category: 'blood',
    src: '/Images/Blood Splatters/4.png',
    alt: 'blood splatter decal',
    defaultSize: 175,
    maxRotate: 15
  },
  {
    category: 'blood',
    src: '/Images/Blood Splatters/5.png',
    alt: 'blood splatter decal',
    defaultSize: 170,
    maxRotate: 15
  },

  // Dead Insects:
  // maggots.png has maxRotate: 10 (+/- 10 deg)
  // other insects have 360 full rotation
  {
    category: 'insect',
    src: '/Images/Dead Insects/fly.png',
    alt: 'dead fly decal',
    defaultSize: 110,
    maxRotate: 360
  },
  {
    category: 'insect',
    src: '/Images/Dead Insects/roach.png',
    alt: 'dead cockroach decal',
    defaultSize: 115,
    maxRotate: 360
  },
  {
    category: 'insect',
    src: '/Images/Dead Insects/maggot.png',
    alt: 'dead maggot decal',
    defaultSize: 105,
    maxRotate: 360
  },
  {
    category: 'insect',
    src: '/Images/Dead Insects/maggots.png',
    alt: 'maggots decal',
    defaultSize: 120,
    maxRotate: 10 // strictly +/- 10 deg rotation as requested!
  },

  // Paint Splatters (gravity physics: max +/- 15 deg)
  {
    category: 'paint',
    src: '/Images/Paint Splatters/1.png',
    alt: 'paint splatter decal',
    defaultSize: 140,
    maxRotate: 15
  },
  {
    category: 'paint',
    src: '/Images/Paint Splatters/2.png',
    alt: 'paint splatter decal',
    defaultSize: 140,
    maxRotate: 15
  },
  {
    category: 'paint',
    src: '/Images/Paint Splatters/3.png',
    alt: 'paint splatter decal',
    defaultSize: 145,
    maxRotate: 15
  },

  // Computer Parts (microchips, RAM, circuits: full rotation)
  {
    category: 'tech',
    src: '/Images/Computer Parts/ram.png',
    alt: 'RAM stick decal',
    defaultSize: 125,
    maxRotate: 360
  },
  {
    category: 'tech',
    src: '/Images/Computer Parts/board.png',
    alt: 'circuit board decal',
    defaultSize: 130,
    maxRotate: 360
  },
  {
    category: 'tech',
    src: '/Images/Computer Parts/microchip.png',
    alt: 'microchip decal',
    defaultSize: 120,
    maxRotate: 360
  },
  {
    category: 'tech',
    src: '/Images/Computer Parts/leds.png',
    alt: 'circuit LEDs decal',
    defaultSize: 115,
    maxRotate: 360
  },
  {
    category: 'tech',
    src: '/Images/Computer Parts/battery.png',
    alt: 'battery decal',
    defaultSize: 120,
    maxRotate: 360
  }
];

/**
 * Rare-only preset pool (20% spawn chance).
 * Same drop-shadow rules as Computer Parts. Full 360 rotation.
 */
export const RARE_PRESETS: DecalPreset[] = [
  {
    category: 'rare',
    src: '/Images/Rare/ciggies.png',
    alt: 'rare ciggies decal',
    defaultSize: 130,
    maxRotate: 360
  },
  {
    category: 'rare',
    src: '/Images/Rare/fry_1.png',
    alt: 'rare fry decal',
    defaultSize: 120,
    maxRotate: 360
  },
  {
    category: 'rare',
    src: '/Images/Rare/fry_2.webp',
    alt: 'rare fry decal 2',
    defaultSize: 120,
    maxRotate: 360
  },
  {
    category: 'rare',
    src: '/Images/Rare/rhino.png',
    alt: 'rare rhino decal',
    defaultSize: 125,
    maxRotate: 360
  },
  {
    category: 'rare',
    src: '/Images/Rare/vape.png',
    alt: 'rare vape decal',
    defaultSize: 115,
    maxRotate: 360
  }
];

/**
 * Global Craziness setting (0.0 to 1.0):
 * - 0.0: No spawns (0 per row)
 * - 0.2: Standard spawns (8 items per gallery row - baseline)
 * - 1.0: Maximum chaos (40 items per gallery row - 5x baseline)
 */
export const DEFAULT_CRAZINESS = 0.8;
export const BASELINE_SPAWNS_PER_ROW = 1;

export function getSpawnsPerRow(craziness: number = DEFAULT_CRAZINESS): number {
  const clamped = Math.min(Math.max(craziness, 0.0), 1.0);
  return Math.round((clamped / 0.2) * BASELINE_SPAWNS_PER_ROW);
}

export function getDecalCountFromCraziness(craziness: number = DEFAULT_CRAZINESS, rowCount: number = 34): number {
  const spawnsPerRow = getSpawnsPerRow(craziness);
  return (rowCount + 1) * spawnsPerRow;
}

interface PlacedDecalMeta {
  item: DecalItem;
  bandIndex: number;
  xPx: number;
  yPx: number;
  radiusPx: number;
}

/**
 * Helper to dynamically detect mobile viewport resolution (<= 768px).
 */
export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768 || (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px)').matches);
}

/**
 * Creates static decals repeating all the way to the bottom of the finalized page,
 * distributed evenly across the whole background wall.
 *
 * Constraints satisfied:
 * 1. Can extend to ALL sides of the screen (from -1% to 101% width) without being cut off.
 * 2. Overlap is strictly capped at no more than 25%, maintaining spacing and distance.
 * 3. Mobile resolution detection: dynamically shrinks all decals by ~48% and reduces spawnrate to 1/10th of PC.
 */
export function createGridDecals(
  rowCount: number = 34,
  craziness: number = DEFAULT_CRAZINESS,
  isMobile: boolean = isMobileViewport()
): DecalItem[] {
  const spawnsPerRow = getSpawnsPerRow(craziness);
  if (spawnsPerRow <= 0 || rowCount <= 0) return [];

  // Canvas dimensions for Euclidean metric space:
  const VIRTUAL_WIDTH_PX = isMobile ? 390 : 1400;
  const VIRTUAL_ROW_HEIGHT_PX = isMobile ? 320 : 520;

  const totalBands = rowCount + 1;
  const bandHeightPct = 100 / totalBands;
  const totalHeightPx = totalBands * VIRTUAL_ROW_HEIGHT_PX;

  // Maximum allowed linear overlap between any two decals (25% max)
  const MAX_ALLOWED_OVERLAP_RATIO = 0.25;

  const placedDecals: PlacedDecalMeta[] = [];
  const bandsMap = new Map<number, PlacedDecalMeta[]>();

  let counter = 0;

  // Generate queue of (b, s) spawns:
  // On PC: all bands with spawnsPerRow items per band.
  // On Mobile: spawnrate is 1/10 of PC, distributed evenly down the page!
  const spawnQueue: Array<{ b: number; s: number }> = [];
  if (!isMobile) {
    for (let b = 0; b < totalBands; b++) {
      for (let s = 0; s < spawnsPerRow; s++) {
        spawnQueue.push({ b, s });
      }
    }
  } else {
    // Exactly 1/10th of PC spawnrate
    const pcTotalSpawns = totalBands * spawnsPerRow;
    const mobileTotalSpawns = Math.max(2, Math.round(pcTotalSpawns * 0.1));
    const stride = totalBands / mobileTotalSpawns;
    for (let i = 0; i < mobileTotalSpawns; i++) {
      const b = Math.min(totalBands - 1, Math.floor(i * stride));
      spawnQueue.push({ b, s: i % 2 });
    }
  }

  for (const { b, s } of spawnQueue) {
    const bandStartPct = b * bandHeightPct;
    // 80/20 weighted preset selection: 20% chance of Rare, 80% normal DECAL_PRESETS
    const isRareSpawn = Math.random() < 0.20;
    let preset: DecalPreset;
    if (isRareSpawn) {
      preset = RARE_PRESETS[Math.floor(Math.random() * RARE_PRESETS.length)];
    } else {
      preset = DECAL_PRESETS[counter % DECAL_PRESETS.length];
    }
    counter++;

    // On PC: 0.65 to 1.10. On Mobile: dynamically shrunk to ~50% (0.34 to 0.57)
    const baseScale = +(0.65 + Math.random() * 0.45).toFixed(2);
    const scale = isMobile
      ? +(baseScale * 0.52).toFixed(2)
      : baseScale;
    const radiusPx = (preset.defaultSize * scale) * 0.5;

    let bestCandidate: { xPct: number; yPct: number; xPx: number; yPx: number; maxOverlap: number } | null = null;
    let accepted = false;

    // Try candidate positions to find one that satisfies the <= 25% overlap rule
    const MAX_ATTEMPTS = 35;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      // Balance spawns between left and right gutters, with failover if congested
      const preferLeft = (b + s) % 2 === 0;
      const tryLeft = attempt < 25 ? preferLeft : !preferLeft;

      let candidateXPct: number;
      if (isMobile) {
        // Mobile resolution: spawnpoints at outer edges, peeking under mobile artwork margins
        if (b === 0) {
          candidateXPct = tryLeft
            ? +((-1.0 + Math.random() * 7.0).toFixed(2)) // [-1.0%, 6.0%]
            : +(94.0 + Math.random() * 7.0).toFixed(2);  // [94.0%, 101.0%]
        } else {
          candidateXPct = tryLeft
            ? +((-1.0 + Math.random() * 8.0).toFixed(2)) // [-1.0%, 7.0%]
            : +(93.0 + Math.random() * 8.0).toFixed(2);  // [93.0%, 101.0%]
        }
      } else {
        // PC Desktop resolution:
        if (b === 0) {
          // Band 0 (Header): Avoid central logo & pinned letters (X in [12%, 88%])
          // Allowed: Left header corner [-1.0%, 12.0%] or Right header corner [88.0%, 101.0%]
          candidateXPct = tryLeft
            ? +((-1.0 + Math.random() * 13.0).toFixed(2))
            : +(88.0 + Math.random() * 13.0).toFixed(2);
        } else {
          // Bands >= 1 (Gallery Grid): The grid container sits centrally at X in [20.0%, 80.0%].
          // Spawnpoint midpoints CANNOT be where the grid sits.
          // Allowed: Left gutter [-1.0%, 19.9%] or Right gutter [80.1%, 101.0%]
          // Decals can naturally overlap UNDER the edge of the grid from these spawnpoints.
          candidateXPct = tryLeft
            ? +((-1.0 + Math.random() * 20.9).toFixed(2))
            : +(80.1 + Math.random() * 20.9).toFixed(2);
        }
      }

      // Distribute vertically within this band (and allow slight vertical bleed into adjacent space)
      const candidateYPct = +(Math.max(0.2, Math.min(99.6, bandStartPct + Math.random() * bandHeightPct)).toFixed(2));

      // Hard Boundary Guards:
      if (isMobile) {
        if (b >= 1 && candidateXPct >= 7.0 && candidateXPct <= 93.0) continue;
        if (b === 0 && candidateXPct >= 6.0 && candidateXPct <= 94.0) continue;
      } else {
        if (b >= 1 && candidateXPct >= 20.0 && candidateXPct <= 80.0) continue;
        if (b === 0 && candidateXPct >= 12.0 && candidateXPct <= 88.0) continue;
      }

      const candidateXPx = (candidateXPct / 100) * VIRTUAL_WIDTH_PX;
      const candidateYPx = (candidateYPct / 100) * totalHeightPx;

      // Check overlap against decals in neighboring bands (b - 1, b, b + 1)
      let candidateMaxOverlap = 0;
      let violatesLimit = false;

      const neighborBands = [b - 1, b, b + 1];
      for (const nb of neighborBands) {
        const neighbors = bandsMap.get(nb);
        if (!neighbors) continue;

        for (const other of neighbors) {
          const dx = candidateXPx - other.xPx;
          const dy = candidateYPx - other.yPx;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const sumRadii = radiusPx + other.radiusPx;

          if (dist < sumRadii) {
            const overlapPx = sumRadii - dist;
            const overlapRatio = overlapPx / sumRadii;

            if (overlapRatio > candidateMaxOverlap) {
              candidateMaxOverlap = overlapRatio;
            }

            if (overlapRatio > MAX_ALLOWED_OVERLAP_RATIO) {
              violatesLimit = true;
              break;
            }
          }
        }
        if (violatesLimit) break;
      }

      if (!violatesLimit) {
        // Perfectly satisfies <= 25% overlap!
        bestCandidate = {
          xPct: candidateXPct,
          yPct: candidateYPct,
          xPx: candidateXPx,
          yPx: candidateYPx,
          maxOverlap: candidateMaxOverlap
        };
        accepted = true;
        break;
      }

      // If not accepted yet, track candidate with lowest maximum overlap
      if (!bestCandidate || candidateMaxOverlap < bestCandidate.maxOverlap) {
        bestCandidate = {
          xPct: candidateXPct,
          yPct: candidateYPct,
          xPx: candidateXPx,
          yPx: candidateYPx,
          maxOverlap: candidateMaxOverlap
        };
      }
    }

    const fallbackLeft = (b + s) % 2 === 0;
    let fallbackXPct: number;
    if (isMobile) {
      if (b === 0) {
        fallbackXPct = fallbackLeft
          ? +((-1.0 + Math.random() * 7.0).toFixed(2))
          : +(94.0 + Math.random() * 7.0).toFixed(2);
      } else {
        fallbackXPct = fallbackLeft
          ? +((-1.0 + Math.random() * 8.0).toFixed(2))
          : +(93.0 + Math.random() * 8.0).toFixed(2);
      }
    } else {
      if (b === 0) {
        fallbackXPct = fallbackLeft
          ? +((-1.0 + Math.random() * 13.0).toFixed(2))
          : +(88.0 + Math.random() * 13.0).toFixed(2);
      } else {
        fallbackXPct = fallbackLeft
          ? +((-1.0 + Math.random() * 20.9).toFixed(2))
          : +(80.1 + Math.random() * 20.9).toFixed(2);
      }
    }

    const fallbackYPct = +(Math.max(0.2, Math.min(99.6, bandStartPct + Math.random() * bandHeightPct)).toFixed(2));

    const finalPosition = bestCandidate || {
      xPct: fallbackXPct,
      yPct: fallbackYPct,
      xPx: (fallbackXPct / 100) * VIRTUAL_WIDTH_PX,
      yPx: (fallbackYPct / 100) * totalHeightPx,
      maxOverlap: 0
    };

    // Calculate effective maxRotate (0 to 360):
    // Preserves blood/paint = 15, maggots.png = 10, other decals = 360 or custom
    const effectiveMaxRotate = preset.maxRotate !== undefined
      ? preset.maxRotate
      : getMaxRotateForImage(preset.src, undefined, preset.category);

    const rot = effectiveMaxRotate < 360
      ? Math.round(Math.random() * (effectiveMaxRotate * 2) - effectiveMaxRotate)
      : Math.floor(Math.random() * 360);

    // Only splatters play with opacity (0.52 to 0.85); all other decals (insects, tech) are 100% opacity
    const isLiquidSplatter = preset.category === 'blood' || preset.category === 'paint';
    const opacity = isLiquidSplatter ? +(0.52 + Math.random() * 0.33).toFixed(2) : 1;

    const rawBandOffset = (finalPosition.yPct - bandStartPct) / (bandHeightPct || 1);
    const bandOffsetPct = +(Math.max(0.05, Math.min(0.95, rawBandOffset)).toFixed(3));

    const newItem: DecalItem = {
      id: `decal-${b}-${s}-${counter}`,
      category: preset.category,
      src: preset.src,
      alt: preset.alt,
      x: finalPosition.xPct,
      y: finalPosition.yPct,
      yPx: finalPosition.yPx,
      bandIndex: b,
      bandOffsetPct,
      rotation: rot,
      maxRotate: effectiveMaxRotate,
      scale,
      opacity,
      createdAt: Date.now()
    };

    const meta: PlacedDecalMeta = {
      item: newItem,
      bandIndex: b,
      xPx: finalPosition.xPx,
      yPx: finalPosition.yPx,
      radiusPx
    };

    placedDecals.push(meta);

    if (!bandsMap.has(b)) {
      bandsMap.set(b, []);
    }
    bandsMap.get(b)!.push(meta);
  }

  return placedDecals.map((meta) => meta.item);
}

export function createStaticDecals(countOrCraziness: number = DEFAULT_CRAZINESS, rowCount: number = 34): DecalItem[] {
  if (countOrCraziness <= 1.0) {
    return createGridDecals(rowCount, countOrCraziness);
  }
  const count = Math.round(countOrCraziness);
  if (count <= 0) return [];

  return createGridDecals(Math.max(1, Math.round(count / 8)), 0.2);
}
